import React, { useEffect, useRef } from 'react';
import { useSocket } from '../../contexts/SocketContext';
import { useWhatsApp } from '../../contexts/WhatsAppContext';

interface PersonalWaEvent {
  type: 'event';
  payload: {
    event: string;
    configId: string;
    data?: {
      sessionStatus?: string;
      reason?: string;
      hint?: string;
      error?: string;
    };
  };
}

interface PersonalWaSessionWatcherProps {
  openConfigId: string | null;
  onDisconnect: (configId: string, status: string, hint: string | null) => void;
  onReconnect: (configId: string) => void;
}

const PersonalWaSessionWatcher: React.FC<PersonalWaSessionWatcherProps> = ({
  openConfigId,
  onDisconnect,
  onReconnect,
}) => {
  const { socket } = useSocket();
  const { whatsappNumbers } = useWhatsApp();
  const previousStatusRef = useRef<Map<string, string>>(new Map());

  const personalConfigs = whatsappNumbers.filter(
    (config) => config.provider === 'personal_web',
  );
  const personalConfigIds = personalConfigs.map((config) => config._id).join(',');

  useEffect(() => {
    if (!socket || personalConfigs.length === 0) return undefined;

    const handleStatusChange = (
      configId: string,
      sessionStatus: string,
      data?: PersonalWaEvent['payload']['data'],
    ) => {
      const previousStatus = previousStatusRef.current.get(configId);
      previousStatusRef.current.set(configId, sessionStatus);

      if (
        previousStatus === 'ready' &&
        (sessionStatus === 'disconnected' || sessionStatus === 'failed') &&
        data?.reason !== 'manual_stop'
      ) {
        const hint = data?.hint || data?.error || data?.reason || null;
        onDisconnect(configId, sessionStatus, hint);
      }

      if (sessionStatus === 'ready' && openConfigId === configId) {
        onReconnect(configId);
      }
    };

    const handlePersonalWaEvent = (msg: PersonalWaEvent) => {
      if (msg?.type !== 'event' || msg.payload?.event !== 'session.status') return;
      const { configId, data } = msg.payload;
      if (!configId || !data?.sessionStatus) return;
      if (!personalConfigs.some((config) => config._id === configId)) return;
      handleStatusChange(configId, data.sessionStatus, data);
    };

    const handleSubscribed = (data: {
      configId: string;
      sessionStatus?: string;
    }) => {
      if (!data.configId || !data.sessionStatus) return;
      if (!personalConfigs.some((config) => config._id === data.configId)) return;
      const previousStatus = previousStatusRef.current.get(data.configId);
      if (!previousStatus) {
        previousStatusRef.current.set(data.configId, data.sessionStatus);
      }
    };

    personalConfigs.forEach((config) => {
      socket.emit('subscribe_personal_wa', { configId: config._id });
      if (!previousStatusRef.current.has(config._id)) {
        previousStatusRef.current.set(
          config._id,
          config.sessionStatus || 'disconnected',
        );
      }
    });

    socket.on('personal_wa_event', handlePersonalWaEvent);
    socket.on('personal_wa_subscribed', handleSubscribed);

    return () => {
      personalConfigs.forEach((config) => {
        socket.emit('unsubscribe_personal_wa', { configId: config._id });
      });
      socket.off('personal_wa_event', handlePersonalWaEvent);
      socket.off('personal_wa_subscribed', handleSubscribed);
    };
  }, [socket, personalConfigIds, openConfigId, onDisconnect, onReconnect]);

  return null;
};

export default PersonalWaSessionWatcher;
